import { type FC, useId } from "react";
import { useFormState } from "react-hook-form";
import TextareaAutosize, { type TextareaAutosizeProps } from "react-textarea-autosize";
import { twMerge } from "tailwind-merge";

import { ErrorMessageBanner } from "./ErrorMessageBanner";

import { t } from "i18n/i18n";

type LabeledTextareaProps = TextareaAutosizeProps & {
  wrapperClassName?: string;
  name: string;
  label: string;
  required?: boolean;
};

export const LabeledTextarea: FC<LabeledTextareaProps> = function LabeledTextareaInternal({
  ref,
  id: idProp,
  className,
  wrapperClassName,
  name,
  label,
  required,
  ...props
}: LabeledTextareaProps & { ref?: React.Ref<HTMLTextAreaElement> }) {
  const randomId = useId();
  const formState = useFormState({ name });
  const id = idProp || `${randomId}-${name}`;
  const errorId = `${id}-error`;

  return (
    <div className={twMerge("flex flex-col", wrapperClassName)}>
      <div className="flex items-center justify-between">
        <label className="font-medium text-black-tint-20" htmlFor={id}>
          {label}
        </label>

        {!required && <div className="font-normal text-black-tint-40">{t("common.optional")}</div>}
      </div>

      <TextareaAutosize
        aria-errormessage={formState.errors[name] ? errorId : undefined}
        aria-invalid={formState.errors[name] ? "true" : "false"}
        className={twMerge(
          "rounded border border-black-tint-90 px-1.5 py-1 font-normal aria-invalid:border-red",
          className,
        )}
        id={id}
        name={name}
        ref={ref}
        {...props}
      />
      <ErrorMessageBanner id={errorId} name={name} />
    </div>
  );
};
